<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import { canManageIdentity, IdentityAdminScreen, useIdentityAdmin } from '@/features/identity-admin'

const { t, locale } = useI18n()
const session = useSession()
const uiLocale = computed(() => locale.value as 'en' | 'hu')

const canManage = computed(() => canManageIdentity(session.principal.value?.authorities ?? []))
const subject = ref<string>()

const admin = useIdentityAdmin({ subject: () => subject.value })
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-6 p-[var(--space-page)]">
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('identityAdmin.title') }}
    </h1>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="!canManage" class="text-[color:var(--color-text-muted)]">
      {{ t('identityAdmin.noAccess') }}
    </p>

    <IdentityAdminScreen
      v-else
      :subject="subject"
      :own-subject="session.principal.value?.subject"
      :sessions="admin.sessions.value"
      :is-loading-sessions="admin.isLoadingSessions.value"
      :sessions-error="admin.sessionsError.value"
      :ending-session-id="admin.endingSessionId.value"
      :end-session-error="admin.endSessionError.value"
      :status="admin.status.value"
      :is-suspending="admin.isSuspending.value"
      :suspend-error="admin.suspendError.value"
      :is-restoring="admin.isRestoring.value"
      :restore-error="admin.restoreError.value"
      :locale="uiLocale"
      @look-up="subject = $event"
      @end-session="admin.endSession($event)"
      :sessions-retrying="admin.retrying.value"
      @retry-sessions="admin.retry()"
      @suspend="admin.suspend()"
      @restore="admin.restore()"
    />
  </main>
</template>
